import {
	CalendarClock,
	CalendarCog,
	ChartPie,
	Clock10,
	FolderKanban,
	FolderLock,
	House,
	Layers,
	Presentation,
	Settings,
	Settings2,
	SquareChartGantt,
	TreePalm,
	User,
	UserPen,
	Users,
	Video,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout, Avatar, Menu, Sidebar, SidebarLink, Topbar } from '@alejandro3c/icy-react-ui';
import logo from '@/assets/logo.svg';
import { Outlet, useLocation } from 'react-router-dom';
import { generateAdminBreadcrumbs } from '@/util/breadcrumbs';

const adminLinks: SidebarLink[] = [
	{
		title: 'Administración',
		collapsible: true,
		items: [
			{ name: 'Usuarios', path: '/users', icon: <User size={20} /> },
			{ name: 'Grupos', path: '/groups', icon: <Users size={20} /> },
		],
	},
	{
		title: 'Gestión',
		collapsible: true,
		items: [
			{ name: 'Oportunidades', path: '/opportunities', icon: <FolderLock size={20} /> },
			{ name: 'Proyectos', path: '/projects', icon: <FolderKanban size={20} /> },
		],
	},
	{
		title: 'Registro',
		collapsible: true,
		items: [
			{ name: 'Actividades', path: '/workActivityTypes', icon: <CalendarCog size={20} /> },
			{ name: 'Registro horario', path: '/timesheet', icon: <Clock10 size={20} /> },
			{ name: 'Carga de trabajo', path: '/workAnalytics', icon: <ChartPie size={20} /> },
		],
	},
	{
		title: 'Vacaciones',
		collapsible: true,
		items: [
			{ name: 'Solicitudes', path: '/dayOffRequests', icon: <TreePalm size={20} /> },
			{ name: 'Balance', path: '/daysOffBalance', icon: <SquareChartGantt size={20} /> },
			{ name: 'Configuración', path: '/daysOffConfiguration', icon: <CalendarClock size={20} /> },
		],
	},
	{
		title: 'Web',
		collapsible: true,
		items: [
			// Web
			{ name: 'Categorías', path: '/webCategories', icon: <Layers size={20} /> },
			{ name: 'Clientes', path: '/webClients', icon: <UserPen size={20} /> },
			{ name: 'Credenciales', path: '/webCredentials', icon: <Video size={20} /> },
			{ name: 'Web Privada', path: '/webPrivate', icon: <Presentation size={20} /> },
		],
	},
	{
		title: 'Configuración',
		collapsible: true,
		items: [
			// Administración
			{ name: 'Ajustes', path: '/settings-google', icon: <Settings size={20} /> },
			{ name: 'Días libres', path: '/settings-days-off', icon: <Settings2 size={20} /> },
			{ name: 'Festivos', path: '/settings-holidays', icon: <House size={20} /> },
		],
	},
];

const webLinks = [
	{
		title: 'Control Horario',
		items: [{ name: 'Registros', path: '/user-timesheet', icon: <Clock10 size={20} /> }],
	},
	{
		title: 'Vacaciones',
		items: [{ name: 'Solicitudes', path: '/dayoff', icon: <TreePalm size={20} /> }],
	},
	{
		title: 'Web',
		items: [
			// Web
			{ name: 'Categorías', path: '/webCategories', icon: <Layers size={20} /> },
			{ name: 'Clientes', path: '/webClients', icon: <UserPen size={20} /> },
			{ name: 'Credenciales', path: '/webCredentials', icon: <Video size={20} /> },
			{ name: 'Web Privada', path: '/webPrivate', icon: <Presentation size={20} /> },
		],
	},
	{
		title: 'Configuración',
		items: [{ name: 'Calendario', path: '/user-config', icon: <Settings size={20} /> }],
	},
];

const userLinks = [
	{
		title: 'Control Horario',
		items: [{ name: 'Registros', path: '/user-timesheet', icon: <Clock10 size={20} /> }],
	},
	{
		title: 'Vacaciones',
		items: [{ name: 'Solicitudes', path: '/dayoff', icon: <TreePalm size={20} /> }],
	},
	{
		title: 'Configuración',
		items: [{ name: 'Calendario', path: '/user-config', icon: <Settings size={20} /> }],
	},
];

const Layout: React.FC = () => {
	const { logout, user, loading } = useAuth();
	const location = useLocation();

	if (loading || !user) {
		return null; // ⛔ Evita renderizar el Sidebar si no hay contexto aún
	}

	const links = user?.role === 'web' ? webLinks : user?.role === 'admin' ? adminLinks : userLinks;
	const firstNameLetter = user?.name ? user.name.charAt(0).toUpperCase() : 'A';

	const topbarInstance = (
		<Topbar
			shadowLevel="none"
			links={[]}
			profileContent={
				<div className="text-secondary-500 flex items-center gap-4">
					<Menu
						activator={<Avatar initials={firstNameLetter} size="md" color="blue" className="cursor-pointer" />}
						position="bottom_end"
						items={[{ label: 'Cerrar sesión', onClick: () => logout() }]}
					/>
				</div>
			}
		/>
	);
	const sidebarInstance = (
		<Sidebar
			colorMode="multiple"
			invertCollapsedIcon={true}
			topContent={<img className="w-full object-contain px-4 py-3" src={logo} />}
			links={links}
		/>
	);

	return (
		<AppLayout breadcrumbs={generateAdminBreadcrumbs(location)} sidebar={sidebarInstance} topbar={topbarInstance}>
			<Outlet />
		</AppLayout>
	);
};

export default Layout;
